/* STAND IN SOMEBODY ELSE'S WORK VIEW — the control, in the work view itself.
 *
 * Brian: *"In my work view I should be able to toggle any of my team members so
 * I can see their work view, they should be able to do the same."* And: Brian
 * stands in a rep's view to manage; reps stand in each other's to coordinate
 * and to compete.
 *
 * ⚠️ IT IS A PERSON-SWITCHER IN THE WORK VIEW, NOT A SETTINGS SCREEN. The point
 * is to change who you are looking at without leaving the thing you are looking
 * at, so it sits at the top of the worklist beside the order and date lenses.
 *
 * ⚠️ AND IT DECIDES NOTHING. `/api/tam` is scope-enforced by `resolveScope()`
 * server-side; this renders the answer. The options come from the server's own
 * rep list, and a refusal is shown in the server's own words. Hard rule 5:
 * `can()` decides what is DRAWN, the server decides what is allowed.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../lib/api.js'
import { readTeamScope, switcherOptions, emptyBookReason } from './teamScope.js'
import { teamFrom } from '../assignmentModel.js'
import ROSTER from '../../../data/sr_team_roster.json'
import styles from './RepWorklist.module.css'

/**
 * `{ scope, status, error }` for the session's team scope.
 *
 * Asks for the whole team once. ⚠️ A 403 IS AN ANSWER, NOT A FAILURE — these
 * routes answer a refusal with `render: "error-not-empty"` and a `detail`
 * written for the reader, so the body is preferred over the thrown message and
 * a transport failure that has no body is reported as a transport failure.
 * That is the same handling TamChainPanel already applies.
 */
export function useTeamScope() {
  const [state, setState] = useState({ scope: null, status: 'loading', error: null })

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()
    api.tamTeam({ rep: '*', signal: ctrl.signal })
      .then((res) => { if (!cancelled) setState({ scope: readTeamScope(res), status: 'done', error: null }) })
      .catch((err) => {
        if (cancelled) return
        const body = err?.body || err?.payload || null
        if (body && (body.detail || body.error || body.render)) {
          setState({ scope: readTeamScope(body), status: 'done', error: null })
        } else {
          setState({ scope: null, status: 'error', error: String(err?.message || err) })
        }
      })
    return () => { cancelled = true; ctrl.abort() }
  }, [])

  return state
}

export default function PersonSwitcher({ scope, status, error, value, onChange, ownRep = '', counts = null }) {
  if (status === 'loading') {
    return <p className={styles.dayScope}><span className="spinner" /> Checking whose work you can see…</p>
  }

  /* A transport failure. Not a denial, and not an empty team. */
  if (status === 'error') {
    return (
      <p className={styles.dayScope} role="alert">
        <b>Could not check whose work you can see.</b> {error} — so this is showing your own only.
        That is a failure to load, not a permission answer.
      </p>
    )
  }

  /* 🔴 THE TEAM, NOT THE PEOPLE WHO HAPPEN TO HOLD RECORDS.
   *
   * The options used to come from the server's TAM rollup alone, which lists
   * only reps who already have a book — so Eric Graham, who holds none, could
   * never be selected, and the first person who needed work given to them was
   * the one person nobody could look at. The assignable roster is folded in,
   * and it is the same `sr_team_roster.json` the Command Center's assignment
   * picker has always used, so the two cannot offer different teams. */
  const options = switcherOptions(scope, { ownRep, roster: teamFrom(ROSTER), counts })
  const canSwitch = options.length > 1
  /* Only people we KNOW hold nothing. `null` means the rollup did not say and
   * nothing counted it — that is not a claim either way. */
  const noBook = options.filter((o) => o.kind !== 'all' && !['(unassigned)', 'unassigned'].includes(o.value.toLowerCase()) && o.hasBook === false).length

  return (
    <div className={styles.personBar}>
      {canSwitch ? (
        <label className={styles.personLabel}>
          <span className={styles.personLabelText}>Whose work</span>
          <select
            className={styles.personSelect}
            value={value || ownRep}
            onChange={(e) => onChange?.(e.target.value)}
          >
            {options.map((o) => (
              /* ⚠️ THE EMPTY BOOK IS MARKED IN THE LIST, NOT HIDDEN FROM IT.
                * Somebody with nothing assigned is exactly who a manager is
                * looking for when they go to hand work out, so the marker is
                * an invitation rather than a warning. */
              <option key={o.value} value={o.value}>
                {o.label}
                {o.hasBook === false ? ' (no records in this scope)' : ''}
                {o.records === null ? ' (count unavailable)' : ''}
                {/* ⚠️ A COUNT ONLY WHERE ONE WAS COUNTED. Never from the
                  * rollup, which is missing people. */}
                {o.hasBook === true && o.records ? ` — ${o.records.toLocaleString()}` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : (
        /* ⚠️ NO SWITCHER IS NOT A BLANK — IT SAYS WHY, IN THE SERVER'S WORDS.
         * `team_visibility_off` is *"a setting, not an empty result"* and
         * `rep_unmapped` is *"not showing you an empty book — it cannot
         * identify one"*. Those sentences are the whole reason the routes
         * answer `error-not-empty`, and a rep who sees a missing control with
         * no explanation concludes the feature is broken. */
        <p className={styles.dayScope}>
          {scope?.reason
            ? <><b>Showing your own work only.</b> {scope.reason}</>
            : <>Showing your own work only.</>}
        </p>
      )}

      {/* 🔴 "UNKNOWN REP" IS THE WRONG SENTENCE FOR SOMEBODY WHO SIMPLY HAS
        * NOTHING YET. The server answers `rep_unknown` for a name that appears
        * on no record — literally true, and it reads as a fault. For a person
        * the roster vouches for it is the normal state, and the rep needs
        * "nothing is assigned", not "unknown". A genuinely unknown name still
        * reads as unknown, so a typo is not softened away. */}
      {emptyBookReason(scope, {
        name: value,
        onRoster: options.some((o) => o.value === value),
      }) && (
        <p className={styles.dayScope}>
          {emptyBookReason(scope, { name: value, onRoster: true })}
        </p>
      )}

      {canSwitch && noBook > 0 && (
        <p className={styles.dayScope}>
          <b>{noBook}</b> people have no championships in this loaded scope and are marked in the list.
          They are selectable so work can be handed to them.
        </p>
      )}

      {/* ⚠️ `degraded` IS NOT `off`. The setting could not be READ, which is a
        * different fact from it being disabled, and claiming the latter would
        * be stating something nobody knows. */}
      {scope?.visibility?.degraded === true && (
        <p className={styles.dayScope} role="alert">
          <b>Team visibility could not be read.</b>{' '}
          {scope.visibility.detail || 'Nobody knows whether it is on or off right now, so this is '
            + 'behaving as though it were off. That is a failure to read the setting, not the setting.'}
        </p>
      )}

      {/* ⚠️ A VERIFICATION RENDER SAYS SO ON SCREEN. The server stamps it so a
        * screenshot can never be mistaken for what a real rep saw; dropping the
        * stamp here would defeat the reason it is on the response. */}
      {scope?.verification && (
        <p className={styles.dayScope} role="alert">
          <b>Not a real session.</b> {scope.verification.note}
          {scope.verification.renderedAs
            ? ` Rendered as ${scope.verification.renderedAs} by ${scope.verification.renderedBy}.`
            : ''}
        </p>
      )}

      {/* Standing in somebody else's view is stated plainly, because a manager
        * who forgets whose list they are reading will act on the wrong one. */}
      {value && value !== '*' && ownRep && value.toLowerCase() !== ownRep.toLowerCase() && (
        <p className={styles.personStanding} role="status">
          You are looking at <b>{value}</b>&rsquo;s work, not your own.
        </p>
      )}
      {value === '*' && (
        <p className={styles.personStanding} role="status">
          Looking at <b>the whole team</b>&rsquo;s work in one place.
        </p>
      )}
    </div>
  )
}
